import { Component, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss']
})
export class ProjectsComponent implements OnInit {
  loading = false;
  saving = false;
  projects: any[] = [];
  staffs: any[] = [];
  clients: any[] = [];
  previewMedia: any = null;

  selectedProject: any = null;
  selectedTeamStaffIds: number[] = [];
  projectMentionState = {
    visible: false,
    query: '',
    start: 0,
    end: 0
  };

  form: any = this.emptyForm();

  statusOptions = ['Planning', 'Active', 'In Progress', 'On Hold', 'Completed'];

  constructor(private projectService: ProjectManagementService, private auth: AuthService) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    forkJoin({
      projects: this.projectService.getProjects(),
      staffs: this.projectService.getStaffs(),
      clients: this.canManageProjects() ? this.projectService.getClientUsers() : of([])
    }).subscribe({
      next: ({ projects, staffs, clients }) => {
        this.projects = projects || [];
        this.staffs = staffs || [];
        this.clients = clients || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load project module.', 'error');
      }
    });
  }

  editProject(project: any): void {
    this.selectedProject = project;
    this.form = {
      name: project?.name || '',
      code: project?.code || '',
      description: project?.description || '',
      mediaAttachments: Array.isArray(project?.mediaAttachments) ? [...project.mediaAttachments] : [],
      clientUserId: project?.clientUserId || '',
      leadStaffId: project?.leadStaffId || null,
      startDate: this.toInputDate(project?.startDate),
      endDate: this.toInputDate(project?.endDate),
      status: project?.status || 'Planning',
      progressPercent: Number(project?.progressPercent ?? 0)
    };

    this.projectService.getProjectTeam(project.id).subscribe({
      next: (members) => {
        this.selectedTeamStaffIds = (members || []).map((m: any) => Number(m.staffId)).filter((id: number) => !Number.isNaN(id));
      },
      error: () => {
        this.selectedTeamStaffIds = [];
      }
    });
  }

  resetForm(): void {
    this.selectedProject = null;
    this.form = this.emptyForm();
    this.selectedTeamStaffIds = [];
  }

  saveProject(): void {
    if (!this.canManageProjects()) return;

    if (!this.form.name?.trim()) {
      Swal.fire('Validation', 'Project name is required.', 'warning');
      return;
    }

    const payload: any = {
      name: this.form.name?.trim(),
      code: this.form.code || null,
      description: this.form.description || null,
      mediaAttachments: this.form.mediaAttachments || [],
      mentionedStaffIds: this.extractMentionedStaffIds(this.form.description),
      clientUserId: this.form.clientUserId || null,
      leadStaffId: this.form.leadStaffId || null,
      startDate: this.form.startDate || null,
      endDate: this.form.endDate || null,
      status: this.form.status || 'Planning',
      progressPercent: Number(this.form.progressPercent || 0)
    };

    this.saving = true;

    const request$ = this.selectedProject
      ? this.projectService.updateProject(this.selectedProject.id, payload)
      : this.projectService.createProject(payload);

    request$.subscribe({
      next: (res) => {
        const projectId = this.selectedProject?.id || res?.id;
        this.assignTeamAfterSave(projectId);
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save project.', 'error');
      }
    });
  }

  private assignTeamAfterSave(projectId: number): void {
    if (!projectId || !this.canManageTeam()) {
      this.finishSave();
      return;
    }

    const leadId = Number(this.form.leadStaffId || 0);
    const members = [...new Set(this.selectedTeamStaffIds || [])]
      .filter((id: number) => !!id)
      .map((staffId: number) => ({
        staffId,
        roleInProject: leadId === staffId ? 'Project Lead' : 'Team Member'
      }));

    this.projectService.assignProjectTeam(projectId, members).subscribe({
      next: () => this.finishSave(),
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Project saved but failed to assign team.', 'error');
      }
    });
  }

  private finishSave(): void {
    this.saving = false;
    Swal.fire('Success', 'Project saved successfully.', 'success');
    this.resetForm();
    this.loadData();
  }

  canManageProjects(): boolean {
    return this.auth.isAdmin() || this.auth.hasPermission('project.manage');
  }

  canManageTeam(): boolean {
    return this.auth.isAdmin() || this.auth.hasPermission('project.team.manage') || this.auth.hasPermission('project.manage');
  }

  statusClass(status: string | null | undefined): string {
    const key = (status || '').toLowerCase().replace(/\\s+/g, '-');
    return `status-${key}`;
  }

  async onMediaSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    await this.appendMediaFiles(files);
    input.value = '';
  }

  async onMediaDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    const files = Array.from(event.dataTransfer?.files || []);
    if (!files.length) return;

    await this.appendMediaFiles(files);
  }

  private async appendMediaFiles(files: File[]): Promise<void> {
    try {
      const attachments = await Promise.all(files.map((file) => this.readFile(file)));
      this.form.mediaAttachments = [...(this.form.mediaAttachments || []), ...attachments];
    } catch {
      Swal.fire('Error', 'Failed to read one or more files.', 'error');
    }
  }

  removeMedia(index: number): void {
    this.form.mediaAttachments = (this.form.mediaAttachments || []).filter((_: any, i: number) => i !== index);
  }

  onProjectDescriptionInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    this.updateMentionState(textarea.value, textarea.selectionStart || 0, this.projectMentionState);
  }

  insertProjectMention(staff: any, textarea: HTMLTextAreaElement): void {
    this.form.description = this.insertMention(this.form.description, staff?.fullName || '', this.projectMentionState);
    textarea.focus();
    this.projectMentionState.visible = false;
  }

  mediaKind(url: string, contentType?: string): 'image' | 'video' | 'audio' | 'pdf' | 'file' {
    const type = String(contentType || '').toLowerCase();
    const source = String(url || '').toLowerCase();
    if (type.startsWith('image/') || source.startsWith('data:image/')) return 'image';
    if (type.startsWith('video/') || source.startsWith('data:video/')) return 'video';
    if (type.startsWith('audio/') || source.startsWith('data:audio/')) return 'audio';
    if (type === 'application/pdf' || source.startsWith('data:application/pdf')) return 'pdf';
    return 'file';
  }

  openPreview(media: any): void {
    this.previewMedia = media || null;
  }

  closePreview(): void {
    this.previewMedia = null;
  }

  private emptyForm(): any {
    return {
      name: '',
      code: '',
      description: '',
      mediaAttachments: [],
      clientUserId: '',
      leadStaffId: null,
      startDate: '',
      endDate: '',
      status: 'Planning',
      progressPercent: 0
    };
  }

  private toInputDate(value: string | Date | null | undefined): string {
    if (!value) return '';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = `${dt.getMonth() + 1}`.padStart(2, '0');
    const d = `${dt.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private readFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        fileName: file.name,
        url: reader.result as string,
        contentType: file.type || 'application/octet-stream'
      });
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private updateMentionState(text: string, caret: number, state: { visible: boolean; query: string; start: number; end: number }): void {
    const value = String(text || '');
    const left = value.slice(0, caret);
    const match = left.match(/(^|\s)@([^\s@]*)$/);
    if (!match) {
      state.visible = false;
      state.query = '';
      return;
    }

    state.visible = true;
    state.query = (match[2] || '').toLowerCase();
    state.start = caret - match[2].length - 1;
    state.end = caret;
  }

  private insertMention(text: string, fullName: string, state: { start: number; end: number }): string {
    const value = String(text || '');
    const before = value.slice(0, state.start);
    const after = value.slice(state.end);
    return `${before}@${fullName} ${after}`.replace(/\s{2,}/g, ' ');
  }

  projectMentionSuggestions(): any[] {
    if (!this.projectMentionState.visible) return [];
    const query = this.projectMentionState.query;
    return this.staffs
      .filter((staff) => !query || String(staff.fullName || '').toLowerCase().includes(query))
      .slice(0, 6);
  }

  private extractMentionedStaffIds(text: string): number[] {
    const value = String(text || '');
    return this.staffs
      .filter((staff) => value.includes(`@${staff.fullName}`))
      .map((staff) => staff.id);
  }
}
